import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthThemeDirective } from 'src/app/core/directives/auth-theme-directive';
import { ConfettiComponent } from 'src/app/shared/components/confetti/confetti.component';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/core/services';
import { AuthMethod, AuthMode } from 'src/app/core/models';
import { filter, forkJoin, Observable, Subscription, take, timer } from 'rxjs';

/**
 * Confirmation page shown after AuthCodeComponent completes a flow.
 *
 * AuthCodeComponent navigates here with `{ mode, method }` in router state,
 * which picks the copy. A direct visit or refresh has no state, so it falls
 * back to login rather than showing an empty success card.
 */
@Component({
  selector: 'app-auth-code-success',
  imports: [AuthThemeDirective, ConfettiComponent],
  templateUrl: './auth-code-success.component.html',
  styleUrl: './auth-code-success.component.scss',
})
export class AuthCodeSuccessComponent implements OnInit, OnDestroy {
  mode!: AuthMode;
  method!: AuthMethod;
  title: string = 'Success!'
  subtitle: string = '';
  actionText: string = '';
  redirectPath: string = '/';
  private redirectSub?: Subscription;


  constructor(
    private router: Router,
    private authService: AuthService
  ) {
    const state = this.router.currentNavigation()?.extras.state ?? history.state;
    if (!state?.mode || !state?.method) {
      this.router.navigate(['/login']);
      return;
    }
    this.mode = state.mode;
    this.method = state.method;
  }

  ngOnInit(): void {
    if (!this.mode) return;

    //Start redirect timeout
    this.constructContent();
    // Dashboard redirects wait for the signed-in user so AuthGuard doesn't bounce them.
    const signedIn$ = this.authService.currentUser$.pipe(filter(user => !!user), take(1));
    const ready$: Observable<unknown> = this.redirectPath === '/dashboard' ? forkJoin([timer(2000), signedIn$]) : timer(2000);
    this.redirectSub = ready$.subscribe(() => this.router.navigate([this.redirectPath]));
  }

  ngOnDestroy(): void {
    this.redirectSub?.unsubscribe();
  }
  
  constructContent(): void {
    switch(this.mode) {
      case(AuthMode.verify):
        this.subtitle = `Your ${this.method === AuthMethod.email ? 'email address' : 'phone number'} has been verified`
        this.actionText = `Redirecting to dashboard...`;
        this.redirectPath = '/dashboard';
        break;
      case(AuthMode.reset):
        this.subtitle = `Your password has been successfully reset`;
        this.actionText = `Redirecting to login...`
        this.redirectPath = '/login';
        break;
      case(AuthMode.login):
        this.subtitle = `You have been successfully logged in`;
        this.actionText = `Logging in...`;
        this.redirectPath = '/dashboard'
        break;
      default:
        break;
    }
  }

}
